// Belt-and-braces draft persistence for the Request-a-Booking modal.
//
// Before RequestBookingPage hands the browser to the hosted card paypage it
// snapshots the filled form here. If the client then CANCELS the payment
// outright, the return page offers "Edit & resubmit", which reopens the modal
// prefilled from this draft — the client never re-types anything. The draft is
// cleared once a request is actually submitted (wallet path, paid card path,
// or a fresh successful submit from the restored draft).
//
// LOCALSTORAGE, NOT SESSIONSTORAGE (item 4 kill/close, 2026-07-19). The draft
// used to live in sessionStorage, which survives the paypage round-trip but
// DIES WITH THE TAB — a browser crash, a killed tab, or a paypage opened into
// a fresh tab mid-payment destroyed the form the client was paying to fund.
// localStorage survives all of those; the staleness job sessionStorage did for
// free is done explicitly by the TTL below (24h, and every submit/discard path
// clears the key). Same module API as before — callers don't change.

const KEY = 'hhc_booking_request_draft';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // stale drafts die after a day

// One transitional read of the pre-switch sessionStorage key, so a client who
// was mid-payment when this shipped still gets their draft back. MIGRATED into
// localStorage (not merely consumed) — callers legitimately read the draft
// several times before it is used (hasBookingDraft on the return page, then
// loadBookingDraft in ClientBookings), so a read must never destroy it.
function readLegacy() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        localStorage.setItem(KEY, raw);
        sessionStorage.removeItem(KEY);
        return raw;
    } catch {
        return null;
    }
}

export function saveBookingDraft({ form, tasks }) {
    try {
        localStorage.setItem(KEY, JSON.stringify({ form, tasks, saved_at: Date.now() }));
    } catch { /* storage full/blocked — the server-side staged request still covers retry */ }
}

export function loadBookingDraft() {
    try {
        const raw = localStorage.getItem(KEY) || readLegacy();
        if (!raw) return null;
        const draft = JSON.parse(raw);
        if (!draft || typeof draft !== 'object' || !draft.form) return null;
        if (Date.now() - Number(draft.saved_at || 0) > MAX_AGE_MS) {
            clearBookingDraft();
            return null;
        }
        return draft;
    } catch {
        return null;
    }
}

export function clearBookingDraft() {
    try { localStorage.removeItem(KEY); } catch { /* nothing to clear */ }
    try { sessionStorage.removeItem(KEY); } catch { /* legacy key */ }
}

export function hasBookingDraft() {
    return loadBookingDraft() != null;
}
